import { useCallback, useEffect, useState } from "react";
import api, { getErrorMessage, rupiah } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

// The auditor's approval of which ledger accounts a domain reconciliation reads (B-454). The server proposes
// candidates from the engagement's chart of accounts and never decides; nothing is pre-selected here either.
// Until an approval exists the domain stays BLOCKED_MAPPING. `groups` approve lists of accounts (sales,
// purchases); `articles` approve one payable (and optional prepaid) account per withholding article.
const split = (text) => (text || "").split(/[\s,;]+/).map((code) => code.trim()).filter(Boolean);
const unique = (codes) => [...new Set(codes)];

export default function AccountMappingApproval({ id, path, title, groups = [], articles = null, onApproved, testid }) {
  const prefix = testid || `mapping-${path}`;
  const [data, setData] = useState(null);
  const [chosen, setChosen] = useState({});
  const [extra, setExtra] = useState({});
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    api.get(`/engagements/${id}/${path}`).then(({ data: result }) => {
      setData(result); setError("");
      const approved = result.approved || {};
      if (articles) setChosen({ payable_accounts: { ...(approved.payable_accounts || {}) }, prepaid_accounts: { ...(approved.prepaid_accounts || {}) } });
      else setChosen(Object.fromEntries(groups.map((g) => [g.key, [...(approved[g.key] || [])]])));
      setNote(approved.note || "");
    }).catch((e) => setError(getErrorMessage(e)));
    // groups/articles are static per domain
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, path]);
  useEffect(() => { load(); }, [load]);

  const toggle = (key, code) => setChosen((c) => ({ ...c, [key]: c[key].includes(code) ? c[key].filter((x) => x !== code) : [...c[key], code] }));
  const pickArticle = (key, article, code) => setChosen((c) => {
    const next = { ...c[key] };
    if (code) next[article] = code; else delete next[article];
    return { ...c, [key]: next };
  });

  const approve = async () => {
    const body = articles ? { payable_accounts: chosen.payable_accounts, prepaid_accounts: chosen.prepaid_accounts }
      : Object.fromEntries(groups.map((g) => [g.key, unique([...(chosen[g.key] || []), ...split(extra[g.key])])]));
    if (note.trim()) body.note = note.trim();
    setBusy(true); setError("");
    try {
      await api.post(`/engagements/${id}/${path}`, body);
      toast.success("Pemetaan akun disetujui");
      setExtra({});
      load();
      onApproved && onApproved();
    } catch (e) { setError(getErrorMessage(e)); } finally { setBusy(false); }
  };

  if (!data) return error ? <p role="alert" className="text-xs text-red-800">{error}</p> : null;
  const approved = data.approved;
  const option = (a) => <option key={a.account_code} value={a.account_code}>{a.account_code} — {a.account_name}</option>;
  return (
    <section className="bg-white border border-border rounded-sm p-3 space-y-3 text-xs" data-testid={prefix}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-head font-semibold text-sm">{title || "Pemetaan akun buku besar"}</span>
        <span className={`px-2 py-0.5 border rounded-sm ${approved ? "bg-green-100 text-green-800 border-green-300" : "bg-amber-100 text-amber-800 border-amber-300"}`} data-testid={`${prefix}-status`}>
          {approved ? `Disetujui · ${approved.approved_by_role} · ${String(approved.approved_at || "").slice(0, 10)}` : "Usulan — belum disetujui auditor"}
        </span>
      </div>
      <p className="text-muted-foreground">{data.basis} Rekonsiliasi domain ini tertahan sampai pemetaan disetujui.</p>
      {!articles && groups.map((g) => (
        <fieldset key={g.key} className="border border-border rounded-sm p-2 space-y-1">
          <legend className="px-1 font-medium">{g.label}{g.required && " *"}</legend>
          {(data[g.candidates] || []).map((a) => (
            <label key={a.account_code} className="flex items-center gap-2">
              <input type="checkbox" checked={(chosen[g.key] || []).includes(a.account_code)} onChange={() => toggle(g.key, a.account_code)}
                     data-testid={`${prefix}-${g.key}-${a.account_code}`} />
              <span className="font-mono">{a.account_code}</span> {a.account_name} · {rupiah(a.balance)}
            </label>
          ))}
          {(data[g.candidates] || []).length === 0 && <p className="text-muted-foreground">Tidak ada kandidat dari bagan akun.</p>}
          {(chosen[g.key] || []).filter((code) => !(data[g.candidates] || []).some((a) => a.account_code === code)).map((code) =>
            <p key={code}>Disetujui sebelumnya: <span className="font-mono">{code}</span></p>)}
          <label className="block">Kode akun lain (pisahkan dengan koma)
            <Input className="h-8 mt-1" value={extra[g.key] || ""} onChange={(e) => setExtra({ ...extra, [g.key]: e.target.value })} data-testid={`${prefix}-${g.key}-extra`} />
          </label>
        </fieldset>
      ))}
      {articles && (
        // B-463: account names make the selects wide; the table scrolls inside its box, never the page.
        <div className="overflow-x-auto" data-testid={`${prefix}-articles`}>
        <table className="w-full audit-table">
          <thead><tr><th>Pasal</th><th>Akun hutang pajak</th><th>Akun pajak dibayar dimuka (opsional)</th></tr></thead>
          <tbody>{articles.map((article) => (
            <tr key={article}>
              <td>{article}</td>
              <td><select aria-label={`Akun hutang ${article}`} className="border p-1 max-w-[14rem]" value={chosen.payable_accounts?.[article] || ""} onChange={(e) => pickArticle("payable_accounts", article, e.target.value)}>
                <option value="">Tidak dipetakan</option>{(data.payable_candidates || []).map(option)}</select></td>
              <td><select aria-label={`Akun dibayar dimuka ${article}`} className="border p-1 max-w-[14rem]" value={chosen.prepaid_accounts?.[article] || ""} onChange={(e) => pickArticle("prepaid_accounts", article, e.target.value)}>
                <option value="">Tidak dipetakan</option>{(data.prepaid_candidates || []).map(option)}</select></td>
            </tr>))}
          </tbody>
        </table>
        </div>
      )}
      <label className="block">Catatan dasar persetujuan
        <Input className="h-8 mt-1" value={note} onChange={(e) => setNote(e.target.value)} data-testid={`${prefix}-note`} />
      </label>
      {error && <p role="alert" className="text-red-800 bg-red-50 border border-red-200 rounded-sm p-2">{error}</p>}
      <Button size="sm" disabled={busy} onClick={approve} data-testid={`${prefix}-approve`}>{approved ? "Setujui ulang pemetaan" : "Setujui pemetaan"}</Button>
    </section>
  );
}
